import { useCallback, useRef, useState } from 'react';
import {
  NotificationCompositionRequest,
  NotificationHistoryDto,
  NotificationPreviewDto,
} from 'src/api/FiretrolApiClient';
import {
  usePreviewNotification,
  useSendWorkflowNotification,
  useSkipWorkflowNotification,
} from 'src/api/queries/workflowNotifications/useWorkflowNotificationMutations';
import { hasExternalRecipient } from 'src/app/workflows/recipients';

interface UseNotificationComposerResult {
  preview: NotificationPreviewDto | null;
  submitting: boolean;
  loadPreview: (eventType?: string) => Promise<NotificationPreviewDto | null>;
  reloadPreview: (external: boolean) => Promise<NotificationPreviewDto | null>;
  send: (
    request: NotificationCompositionRequest,
  ) => Promise<NotificationHistoryDto | null>;
  skip: (
    request: NotificationCompositionRequest,
  ) => Promise<NotificationHistoryDto | null>;
}

export const useNotificationComposer = (
  resourceType: string,
  jobId: number,
  entityId: string,
): UseNotificationComposerResult => {
  const [preview, setPreview] = useState<NotificationPreviewDto | null>(null);
  const eventTypeRef = useRef<string | undefined>(undefined);
  const previewNotification = usePreviewNotification();
  const sendNotification = useSendWorkflowNotification();
  const skipNotification = useSkipWorkflowNotification();

  const { mutateAsync: fetchPreview } = previewNotification;
  const { mutateAsync: sendMutation } = sendNotification;
  const { mutateAsync: skipMutation } = skipNotification;

  const loadPreview = useCallback(
    async (eventType?: string) => {
      setPreview(null);
      eventTypeRef.current = eventType;
      const target = { resourceType, jobId, entityId, eventType };
      let result = await fetchPreview({ ...target, external: false }).catch(
        () => null,
      );
      if (result && hasExternalRecipient(result.to)) {
        result = await fetchPreview({ ...target, external: true }).catch(
          () => null,
        );
      }
      setPreview(result);
      return result;
    },
    [resourceType, jobId, entityId, fetchPreview],
  );

  const reloadPreview = useCallback(
    async (external: boolean) => {
      const result = await fetchPreview({
        resourceType,
        jobId,
        entityId,
        eventType: eventTypeRef.current,
        external,
      }).catch(() => null);
      setPreview(result);
      return result;
    },
    [resourceType, jobId, entityId, fetchPreview],
  );

  const send = useCallback(
    (request: NotificationCompositionRequest) =>
      sendMutation({ resourceType, jobId, entityId, request }),
    [resourceType, jobId, entityId, sendMutation],
  );

  const skip = useCallback(
    (request: NotificationCompositionRequest) =>
      skipMutation({ resourceType, jobId, entityId, request }),
    [resourceType, jobId, entityId, skipMutation],
  );

  return {
    preview,
    submitting: sendNotification.isPending || skipNotification.isPending,
    loadPreview,
    reloadPreview,
    send,
    skip,
  };
};
